import { HelpDocumentEdit } from '../model/help-document-edit';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-help-document-edit',
  templateUrl: './help-document-edit.component.html',
  styleUrls: ['./help-document-edit.component.scss']
})
export class HelpDocumentEditComponent implements OnDestroy
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<HelpDocumentEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public isLocked = false;
  public importFileSelected = false;

  public urlPattern = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  private helpDocumentEdit: HelpDocumentEdit;
  private selectedFileMimeType: string;
  private selectedFile: any = null;

  /** Subscriptions tied to the current `editForm` instance (cleared on form rebuild or `ngOnDestroy`). */
  private formFieldSubscriptions = new Subscription();

  constructor(private fb: UntypedFormBuilder) { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get importFilename(): UntypedFormControl
  {
    return this.editForm.get('importFilename');
  }

  public get type(): UntypedFormControl
  {
    return this.editForm.get('type');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  @Input() public set model(helpDocumentObj: HelpDocumentEdit)
  {
    this.initModel(helpDocumentObj);
  }

  public ngOnDestroy(): void
  {
    this.formFieldSubscriptions.unsubscribe();
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = this.editForm.value;

    // Set Document object
    if (this.importFileSelected && this.type.value === 'DOCUMENT')
    {
      if (objectToPersist.document === null || objectToPersist.document === undefined)
      {
        objectToPersist.document = {};
      }
      objectToPersist.document.name = this.importFilename.value;
      objectToPersist.document.mimeType = this.selectedFileMimeType;
      const base64 = this.selectedFile.split('base64,')[1];
      objectToPersist.document.file = base64;
    }
    else
    {
      objectToPersist.document = null;
    }

    delete objectToPersist.importFilename;

    objectToPersist.type = this.type.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Process the file import.
   */
  public handlePdfInput(files: FileList): void
  {
    const importedFile = files.item(0);

    this.importFileSelected = true;
    this.editForm.importFilename = importedFile.name;
    this.importFilename.setValue(importedFile.name);
    this.selectedFileMimeType = importedFile.type;
    this.editForm.markAsDirty();
    this.setFileFromBlob(importedFile);
  }

  public isValidUri(uri: UntypedFormControl): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return uri.value !== undefined && uri.value && regEx.test(uri.value);
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(helpDocument: HelpDocumentEdit)
  {
    this.formFieldSubscriptions.unsubscribe();
    this.formFieldSubscriptions = new Subscription();
    if (helpDocument === undefined)
    {
      return;
    }

    const typeValue = helpDocument.type !== '' ? helpDocument.type : 'DOCUMENT';

    // Set form — only validate url vs importFilename for the active type (hidden *ngIf rows still exist on the form).
    this.editForm = this.fb.group({
      id: [this.isNew ? null : helpDocument.id],
      name: [
        {
          value: this.isNew ? null : helpDocument.name,
          disabled: false
        },
        {
          validators: [Validators.required]
        }
      ],
      importFilename: [
        {
          // Must be enabled when type is DOCUMENT so required validation counts toward editForm.valid.
          value: this.isNew || !helpDocument.document ? null : helpDocument.document.name,
          disabled: typeValue !== 'DOCUMENT'
        },
        {
          validators: []
        }
      ],
      url: [
        {
          value: this.isNew ? null : helpDocument.url,
          disabled: false
        },
        {
          validators: []
        }
      ],
      type: [
        {
          value: typeValue,
          disabled: !this.isNew
        },
        {
          validators: [Validators.required]
        }
      ],
      enabled: { value: this.isNew ? true : helpDocument.enabled, disabled: false }
    });

    this.applyHelpDocumentTypeValidators(typeValue);
    this.formFieldSubscriptions.add(this.editForm.get('type').valueChanges.subscribe((t: string) =>
      this.applyHelpDocumentTypeValidators(t)));

    this.helpDocumentEdit = helpDocument;
    this.editFormActive = true;
  }

  /** Required URL vs required import file name depending on type, so toggling Enabled can enable Save. */
  private applyHelpDocumentTypeValidators(type: string): void
  {
    if (!this.editForm)
    {
      return;
    }
    const urlCtrl = this.editForm.get('url');
    const importCtrl = this.editForm.get('importFilename');
    const urlValidatorsRequired = [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ];
    if (type === 'URL')
    {
      urlCtrl.enable({ emitEvent: false });
      urlCtrl.setValidators(urlValidatorsRequired);
      importCtrl.clearValidators();
      importCtrl.disable({ emitEvent: false });
    }
    else
    {
      // DOCUMENT: filename is required; URL field is not validated.
      urlCtrl.clearValidators();
      urlCtrl.enable({ emitEvent: false });
      importCtrl.enable({ emitEvent: false });
      importCtrl.setValidators([Validators.required]);
    }
    urlCtrl.updateValueAndValidity({ emitEvent: false });
    importCtrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.selectedFile = reader.result;
    }, false);
    reader.readAsDataURL(file);
  }
}
